import { type FC, useCallback, useEffect, useState } from "react";

import {
  type DashboardPendingInvite,
  acceptInviteAsUser,
  declineInviteAsUser,
  listPendingInvitesAsUser,
} from "../../dashboard-client";
import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { dispatchPendingInvitesRefresh } from "../../hooks/usePendingInviteCount";
import { formatDateTime } from "../../utils/time";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { EmptyState } from "../common/EmptyState";
import { LoadingState } from "../common/LoadingState";
import { PanelSection } from "../common/PanelSection";
import { Text } from "../common/Text";

function formatInviteDate(value: string): string {
  const time = Date.parse(value);
  if (Number.isNaN(time)) {
    return "—";
  }
  return formatDateTime(time);
}

type InviteAction = "accept" | "decline";

const InviteCard: FC<{
  invite: DashboardPendingInvite;
  isProcessing: boolean;
  processingAction: InviteAction | null;
  onRunAction: (token: string, action: InviteAction) => void;
}> = ({ invite, isProcessing, processingAction, onRunAction }) => (
  <div className="space-y-3 rounded-none border border-slate-300 bg-white p-3 shadow-[4px_4px_0_rgba(15,23,42,0.08)] dark:border-slate-700 dark:bg-slate-950/40 dark:shadow-[4px_4px_0_rgba(15,23,42,0.24)]">
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
          {invite.organization_name}
        </span>
        <span className="border border-slate-200 bg-slate-100 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-600 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300">
          {invite.role}
        </span>
      </div>
      <Text as="p">
        Invited by {invite.inviter_name ?? invite.inviter_email ?? "Unknown"}
      </Text>
      <Text as="p">Expires {formatInviteDate(invite.expires_at)}</Text>
    </div>

    <div className="flex gap-2">
      <Button
        size="sm"
        onClick={() => onRunAction(invite.token, "accept")}
        loading={processingAction === "accept"}
        loadingText="Accepting..."
        disabled={isProcessing}
      >
        Accept
      </Button>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => onRunAction(invite.token, "decline")}
        loading={processingAction === "decline"}
        loadingText="Declining..."
        disabled={isProcessing}
      >
        Decline
      </Button>
    </div>
  </div>
);

export const MyInvitesPanel: FC = () => {
  const auth = useDashboardAuth();
  const [invites, setInvites] = useState<DashboardPendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionState, setActionState] = useState<{
    token: string;
    action: InviteAction;
  } | null>(null);

  const refreshInvites = useCallback(async (showLoading: boolean) => {
    if (showLoading) {
      setLoading(true);
    }
    setLoadError(null);
    try {
      const invitesResult = await listPendingInvitesAsUser();
      const pending = invitesResult.data.invites;
      setInvites(pending);
    } catch (error) {
      setLoadError(
        error instanceof Error
          ? error.message
          : "Failed to fetch pending invites.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshInvites(true);
  }, [refreshInvites]);

  const runAction = useCallback(
    async (token: string, action: InviteAction) => {
      setActionError(null);
      setActionState({ action, token });
      try {
        if (action === "accept") {
          await acceptInviteAsUser({ path: { token } });
          await auth.refreshSession();
        } else {
          await declineInviteAsUser({ path: { token } });
        }
        await refreshInvites(false);
        dispatchPendingInvitesRefresh();
      } catch (error) {
        setActionError(
          error instanceof Error
            ? error.message
            : `Failed to ${action} invite.`,
        );
      } finally {
        setActionState(null);
      }
    },
    [auth, refreshInvites],
  );

  const hasInvites = invites.length > 0;

  if (loading) {
    return <LoadingState message="Loading invites..." />;
  }

  if (loadError) {
    return (
      <PanelSection title="My Invites">
        <Alert variant="error">{loadError}</Alert>
        <div className="mt-3">
          <Button variant="secondary" onClick={() => void refreshInvites(true)}>
            Retry
          </Button>
        </div>
      </PanelSection>
    );
  }

  return (
    <div className="space-y-4">
      {actionError && <Alert variant="error">{actionError}</Alert>}

      <PanelSection title="My Invites">
        <p className="mb-4 text-sm text-gray-500 dark:text-white/50">
          Accept or decline invitations sent to your email.
        </p>
        {!hasInvites ? (
          <EmptyState
            title="No pending invites"
            description="When someone invites you to an organization, it will appear here."
            size="sm"
          />
        ) : (
          <div className="space-y-3">
            {invites.map((invite) => {
              const processingAction =
                actionState?.token === invite.token ? actionState.action : null;
              const isProcessing = processingAction !== null;
              return (
                <InviteCard
                  key={invite.id}
                  invite={invite}
                  isProcessing={isProcessing}
                  processingAction={processingAction}
                  onRunAction={(token, action) => void runAction(token, action)}
                />
              );
            })}
          </div>
        )}
      </PanelSection>
    </div>
  );
};
